import React, { useState, useEffect } from 'react';
import { useAuth, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Activity, AlertTriangle, CheckCircle, TrendingDown, TrendingUp,
  Zap, ChevronRight
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export default function StressTestModal({ open, onOpenChange, dealId, currentDscr, minDscr }) {
  const { token } = useAuth();
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [customParams, setCustomParams] = useState({
    interest_rate: 0,
    vacancy_rate: 0,
    rent_change: 0
  });
  const [mode, setMode] = useState('presets'); // presets | custom | result

  useEffect(() => {
    if (open) {
      fetchPresets();
      setResult(null);
      setMode('presets');
    }
  }, [open]);

  const fetchPresets = async () => {
    try {
      const response = await axios.get(`${API}/stress-tests/presets`);
      setPresets(response.data.presets || []);
    } catch (error) {
      console.error('Failed to fetch presets:', error);
    }
  };

  const runPresetTest = async (presetId) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/deals/${dealId}/stress-test/preset/${presetId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(response.data);
      setMode('result');
    } catch (error) {
      toast.error('Failed to run stress test');
    } finally {
      setLoading(false);
    }
  };

  const runCustomTest = async () => {
    const parameters = [];
    
    if (customParams.interest_rate !== 0) {
      parameters.push({
        parameter: 'interest_rate',
        change_type: 'absolute',
        change_value: customParams.interest_rate
      });
    }
    if (customParams.vacancy_rate !== 0) {
      parameters.push({
        parameter: 'vacancy_rate',
        change_type: 'absolute',
        change_value: customParams.vacancy_rate
      });
    }
    if (customParams.rent_change !== 0) {
      parameters.push({
        parameter: 'rent',
        change_type: 'percentage',
        change_value: customParams.rent_change / 100
      });
    }

    if (parameters.length === 0) {
      toast.error('Please set at least one stress parameter');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/deals/${dealId}/stress-test`,
        {
          deal_id: dealId,
          scenario_name: 'Custom Stress Test',
          parameters
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(response.data);
      setMode('result');
    } catch (error) {
      toast.error('Failed to run stress test');
    } finally {
      setLoading(false);
    }
  };

  const alertColors = {
    green: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-500' },
    yellow: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-500' },
    red: { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-500' }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Activity className="h-5 w-5 text-amber-500" />
            Stress Test Analysis
          </DialogTitle>
        </DialogHeader>

        {mode === 'presets' && (
          <div className="space-y-4">
            <p className="text-slate-400 text-sm">
              Test how your deal performs under different stress scenarios.
            </p>

            {/* Preset Scenarios */}
            <div className="space-y-2">
              <Label className="text-slate-300">Preset Scenarios</Label>
              <div className="grid gap-2">
                {presets.map((preset) => (
                  <button
                    key={preset.id}
                    onClick={() => runPresetTest(preset.id)}
                    disabled={loading}
                    className="w-full p-4 rounded-lg bg-slate-800/50 border border-slate-700 hover:border-amber-500/50 transition-all text-left flex items-center justify-between group"
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <Zap className="h-4 w-4 text-amber-500" />
                        <span className="font-medium text-white">{preset.name}</span>
                      </div>
                      <p className="text-sm text-slate-400 mt-1">{preset.description}</p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-slate-600 group-hover:text-amber-500 transition-colors" />
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Test */}
            <div className="pt-4 border-t border-slate-800">
              <Label className="text-slate-300">Custom Stress Test</Label>
              <div className="grid grid-cols-3 gap-4 mt-3">
                <div>
                  <Label className="text-xs text-slate-400">Interest Rate Change</Label>
                  <div className="relative">
                    <Input
                      type="number"
                      step="0.5"
                      value={customParams.interest_rate}
                      onChange={(e) => setCustomParams({...customParams, interest_rate: parseFloat(e.target.value) || 0})}
                      placeholder="+2.0"
                      className="bg-slate-950 border-slate-700 text-white pr-8"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">%</span>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-400">Vacancy Change</Label>
                  <div className="relative">
                    <Input
                      type="number"
                      step="1"
                      value={customParams.vacancy_rate}
                      onChange={(e) => setCustomParams({...customParams, vacancy_rate: parseFloat(e.target.value) || 0})}
                      placeholder="+5"
                      className="bg-slate-950 border-slate-700 text-white pr-8"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">%</span>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-400">Rent Change</Label>
                  <div className="relative">
                    <Input
                      type="number"
                      step="5"
                      value={customParams.rent_change}
                      onChange={(e) => setCustomParams({...customParams, rent_change: parseFloat(e.target.value) || 0})}
                      placeholder="-10"
                      className="bg-slate-950 border-slate-700 text-white pr-8"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">%</span>
                  </div>
                </div>
              </div>
              <Button
                onClick={runCustomTest}
                disabled={loading}
                className="w-full mt-4 bg-amber-500 hover:bg-amber-600 text-slate-900"
              >
                {loading ? 'Running...' : 'Run Custom Stress Test'}
              </Button>
            </div>
          </div>
        )}

        {mode === 'result' && result && (
          <div className="space-y-4">
            {/* Alert Banner */}
            <div className={`p-4 rounded-lg ${alertColors[result.alert_level].bg} border ${alertColors[result.alert_level].border}`}>
              <div className="flex items-center gap-2">
                {result.alert_level === 'green' ? (
                  <CheckCircle className={`h-5 w-5 ${alertColors[result.alert_level].text}`} />
                ) : (
                  <AlertTriangle className={`h-5 w-5 ${alertColors[result.alert_level].text}`} />
                )}
                <span className={`font-semibold ${alertColors[result.alert_level].text}`}>
                  {result.alert_level === 'green' ? 'Deal Passes Stress Test' :
                   result.alert_level === 'yellow' ? 'Caution - Near Limit' :
                   'Warning - Does Not Pass'}
                </span>
              </div>
            </div>

            {/* Scenario Name */}
            <div className="text-center">
              <span className="px-3 py-1 rounded-full bg-slate-800 text-slate-300 text-sm">
                {result.scenario_name}
              </span>
            </div>

            {/* Comparison */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700">
                <div className="text-xs text-slate-400 mb-2">Original</div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-slate-400">DSCR</span>
                    <span className="text-white font-mono">{result.original_dscr?.toFixed(2)}x</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Cash Flow</span>
                    <span className="text-white font-mono">${result.original_cash_flow?.toLocaleString()}</span>
                  </div>
                </div>
              </div>
              <div className={`p-4 rounded-lg ${alertColors[result.alert_level].bg} border ${alertColors[result.alert_level].border}`}>
                <div className="text-xs text-slate-400 mb-2">Stressed</div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-slate-400">DSCR</span>
                    <span className={`font-mono ${alertColors[result.alert_level].text}`}>
                      {result.stressed_dscr?.toFixed(2)}x
                      {result.stressed_dscr < result.original_dscr ? (
                        <TrendingDown className="inline h-3 w-3 ml-1" />
                      ) : (
                        <TrendingUp className="inline h-3 w-3 ml-1" />
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Cash Flow</span>
                    <span className={`font-mono ${result.stressed_cash_flow >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                      ${result.stressed_cash_flow?.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Parameter Changes */}
            {result.parameter_changes?.length > 0 && (
              <div className="p-4 rounded-lg bg-slate-800/30 border border-slate-700">
                <div className="text-xs text-slate-400 mb-2">Changes Applied</div>
                <div className="space-y-1">
                  {result.parameter_changes.map((change, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span className="text-slate-400">{change.parameter}</span>
                      <span className="text-slate-300">
                        {change.original} → {change.stressed}
                        <span className="text-amber-500 ml-2">({change.change})</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Model observations */}
            {result.recommendations?.length > 0 && (
              <div>
                <Label className="text-slate-300">Model Observations</Label>
                <ul className="mt-2 space-y-2">
                  {result.recommendations.map((rec, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-400">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-1.5 flex-shrink-0"></span>
                      {rec}
                    </li>
                  ))}
                </ul>
                <p className="mt-3 text-xs text-slate-500">
                  Stress-test outputs are estimates based on supplied assumptions,
                  not financial or investment advice.
                </p>
              </div>
            )}

            {/* Back Button */}
            <Button
              variant="outline"
              onClick={() => setMode('presets')}
              className="w-full text-slate-300 border-slate-700"
            >
              Run Another Test
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
